import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { api } from '../services/api';
import {
  MessageSquare,
  Users,
  Clock,
  CheckCircle,
  Building2,
  Activity
} from 'lucide-react';

interface DashboardStats {
  totalConversations: number;
  activeConversations: number;
  queuedConversations: number;
  closedConversations: number;
  totalDepartments?: number;
  totalAgents?: number;
}

interface SystemInfo {
  environment: string;
  environmentLabel: string;
  whatsappConfigured: boolean;
  whatsappStatus: string;
  whatsappProvider: string | null;
}

export const Dashboard: React.FC = () => {
  const { agent, isAdmin } = useAuth();
  const { systemName } = useSettings();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalConversations: 0,
    activeConversations: 0,
    queuedConversations: 0,
    closedConversations: 0,
  });
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [periodFilter, setPeriodFilter] = useState<'week' | 'month' | 'year'>('week');

  useEffect(() => {
    loadStats();
    if (isLoading) {
      loadSystemInfo();
    }
  }, [periodFilter]);

  const loadStats = async () => {
    try {
      setIsLoadingStats(true);
      // Calculate date range based on filter
      const now = new Date();
      let startDate = new Date();

      switch (periodFilter) {
        case 'week':
          startDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(now.getMonth() - 1);
          break;
        case 'year':
          startDate.setFullYear(now.getFullYear() - 1);
          break;
      }

      // Load conversations and calculate stats
      const response = await api.getConversations({ limit: '1000' }); // Get more for stats

      // Filter conversations by period
      const filteredConversations = response.data.conversations.filter((c: any) => {
        const conversationDate = new Date(c.startedAt || c.createdAt);
        return conversationDate >= startDate;
      });

      const stats: DashboardStats = {
        totalConversations: filteredConversations.length,
        activeConversations: filteredConversations.filter((c: any) => c.status === 'ACTIVE').length,
        queuedConversations: filteredConversations.filter((c: any) => c.status === 'QUEUED').length,
        closedConversations: filteredConversations.filter((c: any) => c.status === 'CLOSED').length,
      };

      setStats(stats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingStats(false);
    }
  };

  const loadSystemInfo = async () => {
    try {
      const info = await api.getSystemInfo();
      setSystemInfo(info);
    } catch (error) {
      console.error('Failed to load system info:', error);
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
    onClick?: () => void;
  }> = ({ title, value, icon, color, onClick }) => (
    <div
      onClick={onClick}
      className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-2 border-transparent transition-all duration-200 ${
        onClick
          ? 'cursor-pointer hover:shadow-lg hover:border-blue-400 hover:-translate-y-0.5'
          : ''
      }`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold text-white">Bem-vindo, {agent?.name}</h1>
        <p className="mt-2 text-gray-400">
          {isAdmin() ? 'Painel de administração' : `Departamento: ${agent?.department?.name || 'N/A'}`}
        </p>
        <p className="mt-1 text-gray-500 text-sm">
          Sistema de Atendimento ao Cliente - {systemName}
        </p>
      </div>

      {/* Statistics Section with Period Filter */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Estatísticas</h2>
          <select
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value as 'week' | 'month' | 'year')}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
          >
            <option value="week">Última Semana</option>
            <option value="month">Último Mês</option>
            <option value="year">Último Ano</option>
          </select>
        </div>

        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative ${isLoadingStats ? 'opacity-50 pointer-events-none' : ''}`}>
          {isLoadingStats && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}
          <StatCard
            title="Total de Atendimentos"
            value={stats.totalConversations}
            icon={<MessageSquare className="w-6 h-6 text-white" />}
            color="bg-blue-500"
            onClick={() => navigate('/conversations')}
          />
          <StatCard
            title="Atendimentos Ativos"
            value={stats.activeConversations}
            icon={<Activity className="w-6 h-6 text-white" />}
            color="bg-green-500"
            onClick={() => navigate('/conversations?status=ACTIVE')}
          />
          <StatCard
            title="Na Fila"
            value={stats.queuedConversations}
            icon={<Clock className="w-6 h-6 text-white" />}
            color="bg-yellow-500"
            onClick={() => navigate('/conversations?status=QUEUED')}
          />
          <StatCard
            title="Fechados"
            value={stats.closedConversations}
            icon={<CheckCircle className="w-6 h-6 text-white" />}
            color="bg-gray-500"
            onClick={() => navigate('/conversations?status=CLOSED')}
          />
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Ações Rápidas</h2>
        <div className={`grid grid-cols-1 ${isAdmin() ? 'md:grid-cols-2' : 'md:grid-cols-1'} gap-4`}>
          <button
            onClick={() => navigate('/conversations')}
            className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-2 border-transparent hover:shadow-lg hover:border-blue-400 hover:-translate-y-0.5 transition-all duration-200 text-left"
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-100 rounded-full">
                <MessageSquare className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">Ver Atendimentos</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Gerenciar atendimentos</p>
              </div>
            </div>
          </button>

          {isAdmin() && (
            <button
              onClick={() => navigate('/departments')}
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-2 border-transparent hover:shadow-lg hover:border-purple-400 hover:-translate-y-0.5 transition-all duration-200 text-left"
            >
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                  <Building2 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">Departamentos</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Gerenciar setores</p>
                </div>
              </div>
            </button>
          )}
        </div>
      </div>

      {/* Info Cards */}
      <div className={`grid grid-cols-1 ${isAdmin() ? 'md:grid-cols-2' : ''} gap-6`}>
        {/* System Info - Only for Admin */}
        {isAdmin() && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Activity className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
              Informações do Sistema
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Status do Sistema:</span>
                <span className="font-medium text-green-600 dark:text-green-400">✓ Online</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Modo de Operação:</span>
                <span className={`font-medium ${
                  systemInfo?.environment === 'production'
                    ? 'text-green-600 dark:text-green-400'
                    : systemInfo?.environment === 'staging'
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-yellow-600 dark:text-yellow-400'
                }`}>
                  {systemInfo?.environmentLabel || 'Carregando...'}
                </span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600 dark:text-gray-400">Integração WhatsApp:</span>
                <div className="text-right">
                  <span className={`font-medium ${
                    systemInfo?.whatsappConfigured
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}>
                    {systemInfo?.whatsappStatus || 'Carregando...'}
                  </span>
                  {systemInfo?.whatsappProvider && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {systemInfo.whatsappProvider}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User Info */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
            Informações do Usuário
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Nome:</span>
              <span className="font-medium text-gray-900 dark:text-white">{agent?.name}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Email:</span>
              <span className="font-medium text-gray-900 dark:text-white">{agent?.email}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Função:</span>
              <span className="font-medium text-blue-600 dark:text-blue-400">{agent?.role}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600 dark:text-gray-400">Departamento:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {agent?.department?.name || 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Getting Started */}
      {stats.totalConversations === 0 && isAdmin() && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-3">
            Bem-vindo ao {systemName}
          </h3>
          <p className="text-gray-400 mb-4">
            Este é um sistema de atendimento ao cliente integrado com WhatsApp.
            {!systemInfo?.whatsappConfigured && (
              <span> Como você está em modo de {systemInfo?.environmentLabel?.toLowerCase() || 'desenvolvimento'} sem integração WhatsApp ativa, não há atendimentos no momento.</span>
            )}
            {systemInfo?.whatsappConfigured && (
              <span> A integração com WhatsApp está ativa via {systemInfo?.whatsappProvider}.</span>
            )}
          </p>
          {!systemInfo?.whatsappConfigured && (
            <div className="bg-gray-900 border border-gray-700 rounded p-4 text-sm text-gray-300">
              <p className="font-medium mb-2 text-white">Para começar:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Configure as credenciais do WhatsApp Business API no arquivo .env do backend</li>
                <li>Os atendimentos aparecerão automaticamente quando clientes enviarem mensagens</li>
                <li>Você pode gerenciar departamentos na seção "Departments"</li>
                <li>Explore a interface e se familiarize com as funcionalidades</li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
